import React, { Suspense } from "react";
import { createRoot } from "react-dom/client";
import "./assets/css/tailwind.output.css";
import "./assets/css/v2-tokens.css";
import "./i18n"; // Initialize i18n
import App from "./App";
import ThemedSuspense from "./components/ThemedSuspense";
import * as serviceWorker from "./serviceWorker";
import AuthProvider from "./context/AuthContext";
import { Toast } from "@heroui/react";
import { ThemeProvider as NextThemesProvider } from "next-themes";
import { ThemeBridge } from "./v2/ThemeBridge";
import { MotionProvider } from "./v2/MotionProvider";
import posthog from "posthog-js";
import { PostHogProvider } from "@posthog/react";

// A tab opened before a deploy asks for code files that the deploy
// replaced. Firebase answers those with index.html, so the next page fails
// with "ChunkLoadError" (seen in production after the 2026-10-09 deploys).
// Reload once to pick up the new version; at most once a minute, so a real
// network problem cannot cause a reload loop.
const CHUNK_ERROR = /ChunkLoadError|Loading (CSS )?chunk \S+ failed/;
const reloadForNewVersion = (text) => {
  if (!CHUNK_ERROR.test(String(text || ""))) return;
  try {
    const last = Number(sessionStorage.getItem("hc_chunk_reload") || 0);
    if (Date.now() - last < 60000) return;
    sessionStorage.setItem("hc_chunk_reload", String(Date.now()));
  } catch {
    return; // no storage, no loop guard: leave the page as it is
  }
  window.location.reload();
};
window.addEventListener("error", (e) =>
  reloadForNewVersion(`${e?.message} ${e?.error?.name}`),
);
window.addEventListener("unhandledrejection", (e) =>
  reloadForNewVersion(`${e?.reason?.name} ${e?.reason?.message}`),
);

posthog.init("phc_12OmMZ38L0hiDxcO4jtNIdqT2sglFWukBbDbfvBie3D", {
  api_host: "https://t.hyperclapper.com",
  ui_host: "https://us.posthog.com",
  defaults: "2026-05-30",
  person_profiles: "identified_only",
});

const container = document.getElementById("root");
const root = createRoot(container);

root.render(
  <React.StrictMode>
    <PostHogProvider client={posthog}>
      {/* HeroUI v3 toasts (2026-10-15; were v2). Calls go through
          addToast in src/v2/ui/toast.js, which also sets the timeout.
          Bottom centre keeps them clear of the Intercom launcher in the
          bottom-right corner. z-[300] keeps them above every overlay: the
          channel modals are z-[100] and the mobile sidebar drawer z-[210],
          and the region's default z-50 hid a toast fired while a modal
          was open behind its backdrop (owner feedback 2026-09-28). */}
      <Toast.Provider placement="bottom" width={600} className="z-[300]" />
      <NextThemesProvider attribute="class" defaultTheme="light">
        <ThemeBridge />
        <MotionProvider>
          <AuthProvider>
            <Suspense fallback={<ThemedSuspense />}>
              <App />
            </Suspense>
          </AuthProvider>
        </MotionProvider>
      </NextThemesProvider>
    </PostHogProvider>
  </React.StrictMode>,
);

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister();
